/**
 * 約定履歴ティッカー 1 行の推定エリア・売買区分 (AC-BIN-031)。
 *
 * design: enechain-labs/ebalance-prototype の `bidding/intraday-price.html`
 * (`sideBadgeHtml` / `areaHtml`)。 1 約定 = 1 行の表で、 売買区分と売りエリア・買いエリアを
 * それぞれ列に出す。 項目名は列見出しが持つ (#10113)。
 */
import { useTranslation } from 'react-i18next'

import { Box, Text } from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { areaGroupLabel } from '~/pages/IntradayBidding/models/marketAreaGroupLabel'
import type {
  TickerEstimate,
  TickerEstimatedArea,
  TickerEstimatedSide,
} from '~/pages/IntradayBidding/models/marketTickerEstimate'

/** 売買区分バッジ (推定)。 推定中はグレーのバッジ、 特定できない時はバッジを出さず「—」。 */
export const TickerEstimatedSideBadge = ({
  side,
}: {
  side: TickerEstimatedSide
}): React.ReactElement => {
  const { t } = useTranslation()
  switch (side.kind) {
    case 'estimating':
      return (
        <Badge flexShrink={0} status="neutral">
          {t('page:biddingIntraday.market.ticker.estimating')}
        </Badge>
      )
    case 'unidentified':
      // 売/買 バッジと同じ幅に置き、 価格の位置を揃える (design の .im-tk-side-unknown)
      return (
        <Text
          as="span"
          justifyContent="center"
          display="inline-flex"
          minWidth="20px"
          color="Text.midEmphasis"
        >
          —
        </Text>
      )
    case 'identified':
      return <TradeSideLabel side={side.side} />
  }
}

/**
 * 視覚上は隠し、 読み上げにだけ残す (design の `.im-tk-area-k { display:none }` に当たる)。
 * 行の値だけでは売りか買いか読み上げで分からないため、 項目名を行にも持たせる。
 */
const VISUALLY_HIDDEN_CSS = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const

/** 売りエリア (推定) または買いエリア (推定) の 1 列分。 */
export const TickerEstimatedAreaCell = ({
  side,
  estimate,
}: {
  side: 'sell' | 'buy'
  estimate: TickerEstimate
}): React.ReactElement => {
  const { t } = useTranslation()
  const label =
    side === 'sell'
      ? t('page:biddingIntraday.market.ticker.sellAreaEstimated')
      : t('page:biddingIntraday.market.ticker.buyAreaEstimated')
  const area: TickerEstimatedArea =
    side === 'sell' ? estimate.sellArea : estimate.buyArea
  const value =
    area.kind === 'estimating'
      ? t('page:biddingIntraday.market.ticker.estimating')
      : area.kind === 'unidentified'
        ? '—'
        : areaGroupLabel(t, area.areaGroup)
  return (
    // 推定中・「—」 は値と見分けるため 1 段薄くする (design の .is-tentative)
    <Text
      as="span"
      overflow="hidden"
      minWidth={0}
      color={
        area.kind === 'identified' ? 'Text.highEmphasis' : 'Text.midEmphasis'
      }
      whiteSpace="nowrap"
      textOverflow="ellipsis"
    >
      <Box as="span" css={VISUALLY_HIDDEN_CSS}>
        {label}
      </Box>
      {value}
    </Text>
  )
}
